import { Observable } from "rxjs";
import { map } from "rxjs/operators";

import {
    Injectable, inject
} from "@angular/core";
import { RequestUtil } from "@shopliftr/common-js/shared";
import {
    TeamProgress, TeamProgressSubmission, UserEventProgress, UserProgressSubmission
} from "@shopliftr/common-js/admin";
import { IInternalApiClient } from "../../../../interfaces/internal-api-client";
import { InternalApiRequestService } from "../../request/internal-api/internal-api-request.service";

/**
 * API client service for task event progress on the `task-events` endpoint.
 *
 * Submit-only client with no CRUD surface. Each method posts a `SubmitRequest` (action `CLIENT_SUBMIT`) wrapping a
 * progress submission to a `/v2/task-events/*` route and unwraps the `SubmitResponse` payload into its model.
 *
 * @example
 * ```typescript
 * private readonly _taskEventsApiClientService = inject(TaskEventsApiClientService);
 *
 * const submission = new TeamProgressSubmission();
 * submission.teamId = "team-123";
 * submission.startTime = start.getTime();
 * submission.endTime = end.getTime();
 * this._taskEventsApiClientService.getTeamProgress$(submission).subscribe(this._onTeamProgress);
 * ```
 *
 * **Service Type:** API Client Service (`IInternalApiClient`, submit-only)
 * **Provided:** Root-level singleton
 */
@Injectable({
    providedIn: "root"
})
export class TaskEventsApiClientService implements IInternalApiClient {

    readonly basePath = "task-events";

    private readonly _internalApiRequestService = inject(InternalApiRequestService);

    /**
     * Retrieve the progress of a team over a time window. An omitted `teamId` falls back to the caller's team
     * (the backend responds 400 when the caller has no team).
     *
     * @param submission - The team progress submission carrying the optional team ID and the start and end times
     * (epoch milliseconds)
     * @returns Observable emitting the `TeamProgress` for the requested window
     */
    getTeamProgress$(submission: TeamProgressSubmission): Observable<TeamProgress> {

        const request = RequestUtil.createSubmitRequest(submission);
        return this._internalApiRequestService.submit$<TeamProgressSubmission, TeamProgress>(
            request, `${this.basePath}/team-progress`
        ).pipe(map((response) => response.payload));
    }

    /**
     * Retrieve the task event progress of a user over a time window. An omitted `userId` falls back to the calling user.
     *
     * @param submission - The user progress submission carrying the optional user ID and the start and end times
     * (epoch milliseconds)
     * @returns Observable emitting the `UserEventProgress` for the requested window
     */
    getUserProgress$(submission: UserProgressSubmission): Observable<UserEventProgress> {

        const request = RequestUtil.createSubmitRequest(submission);
        return this._internalApiRequestService.submit$<UserProgressSubmission, UserEventProgress>(
            request, `${this.basePath}/user-progress`
        ).pipe(map((response) => response.payload));
    }
}
